import * as React from 'react';
import {
  flexRender,
  type Column,
  type Row,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  table: TanstackTable<TData>;
  /** Shown in place of rows when the table is empty. */
  emptyState?: React.ReactNode;
  /** Extra classes applied to body rows — a string for all, or per-row via a fn. */
  rowClassName?: string | ((row: Row<TData>) => string);
  /** Render expandable detail content below a row when it's expanded. */
  renderSubComponent?: (row: Row<TData>) => React.ReactNode;
  className?: string;
}

/**
 * Shared list-table shell: a rounded card, a subtle horizontal-scroll mask,
 * a muted sticky-feel header, roomy cells and a first-class empty state. Pass a
 * TanStack `table` instance so each page keeps its own columns, sorting and
 * selection while the chrome stays consistent site-wide.
 */
export function DataTable<TData>({
  table,
  emptyState,
  rowClassName,
  renderSubComponent,
  className,
}: DataTableProps<TData>) {
  const colSpan = table.getVisibleLeafColumns().length;
  const rows = table.getRowModel().rows;

  // Fade the scroller's content at whichever edge has more to reveal — a CSS
  // mask reads correctly in both themes (no colored overlay to mismatch).
  const tableRef = React.useRef<HTMLTableElement>(null);
  const updateEdges = React.useCallback(() => {
    const el = tableRef.current?.parentElement;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const left = scrollLeft > 1 ? '2.5rem' : '0px';
    const right = scrollLeft + clientWidth < scrollWidth - 1 ? '2.5rem' : '0px';
    const mask = `linear-gradient(to right, transparent, #000 ${left}, #000 calc(100% - ${right}), transparent)`;
    el.style.setProperty('mask-image', mask);
    el.style.setProperty('-webkit-mask-image', mask);
  }, []);
  React.useEffect(() => {
    const el = tableRef.current?.parentElement;
    updateEdges();
    el?.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el?.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges, rows.length]);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border bg-card duration-300 animate-in fade-in',
        className
      )}
    >
      <Table ref={tableRef}>
        <TableHeader className="[&_tr]:border-b [&_tr]:bg-muted/40">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="h-11 px-4 text-xs font-medium text-muted-foreground [&:has([role=checkbox])]:pr-2"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() && 'selected'}
                  className={cn(
                    'transition-colors hover:bg-muted/40',
                    typeof rowClassName === 'function' ? rowClassName(row) : rowClassName
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="px-4 py-3 [&:has([role=checkbox])]:pr-2"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {renderSubComponent && (
                  <TableRow className="border-0 hover:bg-transparent">
                    <TableCell colSpan={colSpan} className="border-0 p-0">
                      {/* grid 0fr↔1fr animates the reveal height smoothly both
                          ways (open and close) with the content kept mounted. */}
                      <div
                        data-state={row.getIsExpanded() ? 'open' : 'closed'}
                        className={cn(
                          'grid transition-[grid-template-rows] duration-200 ease-out',
                          row.getIsExpanded() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                        )}
                      >
                        <div className="overflow-hidden">
                          <div className="bg-muted/30">{renderSubComponent(row)}</div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={colSpan} className="h-64 p-0">
                {emptyState ?? (
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Inbox className="size-6 text-muted-foreground" />
                      </EmptyMedia>
                      <EmptyTitle>No results</EmptyTitle>
                      <EmptyDescription>There's nothing to show here yet.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** Sortable column header used inside DataTable column defs. */
export function DataTableColumnHeader<TData>({
  column,
  label,
  className,
}: {
  column: Column<TData, unknown>;
  label: string;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return <span className={className}>{label}</span>;
  }
  const sorted = column.getIsSorted();
  const Icon = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ChevronsUpDown;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => column.toggleSorting(sorted === 'asc')}
      className={cn(
        '-ml-2 h-8 gap-1.5 px-2 text-xs font-medium text-muted-foreground hover:text-foreground',
        sorted && 'text-foreground',
        className
      )}
    >
      {label}
      <Icon className="size-3.5" />
    </Button>
  );
}
